import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { StyleSheet, View } from "react-native";
import { dataConstants } from "services/redux/data/constants";
import { Column, Container, Grid, Row } from 'components/atoms/BootstrapGrid'
import Translate from "utils/translate";
import ThemedBlock from "components/atoms/ThemedBlock";
import _ from "lodash";
import ThemedBlockTitle from 'components/atoms/ThemedBlockTitle'
import ThemedList from 'components/atoms/ThemedList'
import ManagementDashboardComponentMeetingRoomsTimeslots from "./buildingMeetingRoomsTimeslots"
import ManagementDashboardModalMeetingRoomDetails from "../modals/buildingMeetingRoomDetails";
import * as Progress from 'react-native-progress';
import i18n from "utils/i18n";
import { colorList } from "utils/colors";
import Text from "theme/Text";
import ThemedPressable from 'components/atoms/ThemedPressable'
import useForegroundPolling from 'utils/hooks/use-foreground-polling'
import { dashboardMeetingRooms, dashboardBookings } from 'utils/helper-management-dashboard'
import Button from 'theme/Button'
import FetchManagementTimeslotCapacity from 'utils/fetch/management/FetchManagementTimeslotCapacity'
import HelperDate, { dateFormat } from 'utils/helper-date'

const ManagementDashboardComponentMeetingRooms = ( { building = null } ) => {
	const [ showAllTimeslots, setShowAllTimeslots ] = useState(false);
	const [ now, setNow ] = useState( () => {
		return HelperDate.date();
	});
	const [ roomTimeslotModalData, setRoomTimeslotModalData ] = useState(null);
	const floors = useSelector( ( state ) => {
		return state.data[dataConstants.FLOORS];
	});
	const rooms = useSelector( ( state ) => {
		return state.data[dataConstants.ROOMS];
	});
	const allRooms = useMemo( () => {
		return dashboardMeetingRooms(building, floors, rooms, now, showAllTimeslots);
	}, [ building, floors, rooms, now, showAllTimeslots ]);
	const requestKey = JSON.stringify([building?.guid, now.format(dateFormat.DATE), allRooms.map( ( room ) => {
		return [room.guid, room.all_timeslots.map( ( slot ) => {
			return slot.guid;
		})];
	})]);
	const { data, error, loading: roomTimeslotBookingLoading, retry } = useForegroundPolling({
		requestKey,
		request: ( callbacks ) => {
			setNow(HelperDate.date());
			FetchManagementTimeslotCapacity.fetch({ room: allRooms, dates: [HelperDate.date().format(dateFormat.DATE)], ...callbacks });
		},
	});
	const roomTimeslotBookings = useMemo( () => {
		return dashboardBookings(data);
	}, [ data ]);
	const meetingRooms = allRooms.filter( ( room ) => {
		return room.timeslots.length > 0;
	});

	let roomTimeslotModal = null;
	if( roomTimeslotModalData !== null ) {
		roomTimeslotModal = (
			<ManagementDashboardModalMeetingRoomDetails
				onClose={ setRoomTimeslotModalData.bind( this, null ) }
				building={ building }
				room={ roomTimeslotModalData.room }
				timeslot={ roomTimeslotModalData.timeslot }
				timeslots={  _.get( roomTimeslotModalData.room , 'all_timeslots', [] ) }
				bookings={ _.get( roomTimeslotBookings, roomTimeslotModalData.room.guid, null ) }
			/>
		);
	}

	if( meetingRooms.length === 0 && !error ) {
		return null;
	}
	
	return (
		<View>
			{ roomTimeslotModal }
			{ error && (
				<View>

					<Text>{ i18n.t('management:generic.load_error') }</Text>
					<Button onPress={ retry }>

						{ i18n.t('management:generic.retry') }
					</Button>
				</View>
			)}
			
			<Grid>
				<Container>
					<Row style={ styles.rowMarginBottom6 } justify={'flex-start'} align={'center'}>
						<Column>
							<ThemedBlockTitle smallPadding style={ styles.themedBlockTitlePaddingBottom0 }>
								{ i18n.t( 'management:pages.dashboard.titles.meeting_rooms', { name: Translate.field( building, 'title' ) } ) }
							</ThemedBlockTitle>
						</Column>
						<Column>
							<ThemedPressable onPress={ setShowAllTimeslots.bind( this, !showAllTimeslots ) }>
								<Text style={ styles.textShowAllTimeSlots }>
									{ !showAllTimeslots ? i18n.t( 'management:pages.dashboard.meeting_rooms.show_all_timeslots' ) : i18n.t( 'management:pages.dashboard.meeting_rooms.hide_all_timeslots' ) }
								</Text>
							</ThemedPressable>
						</Column>
					</Row>
					
					<Row innerSpan={{ xs: 12, md: 4 }}>
						{ _.map( meetingRooms, ( meetingRoom, key ) => {
							return (
								<Column
									key={ meetingRoom.guid }>

									<ThemedBlock
										shadow
										header={ i18n.t( 'management:pages.dashboard.meeting_rooms.title', {name: Translate.field( meetingRoom, 'title' )} ) }>

										<View style={ styles.viewHeight12MarginBottom4 }>
											{ roomTimeslotBookingLoading !== false && (
												<Progress.Bar
													indeterminate={ true }
													height={4}
													width={ null }
													animated={ true }
													color={ colorList.ORANGE.light }
													borderRadius={0}
													borderWidth={0}
												/>
											)}
										</View>

										<ThemedList
											data={ _.get( meetingRoom, 'timeslots', [] ) }
											dividerEnabled={true}
											renderItem={( {item = [] } ) => {
												return (
													<ManagementDashboardComponentMeetingRoomsTimeslots
														onPress={ setRoomTimeslotModalData.bind( this, { room: meetingRoom, timeslot: item }) }
														timeslot={ item }
														now={ now }
														loading={ roomTimeslotBookingLoading || !!error }
														bookings={ _.get( roomTimeslotBookings, [ meetingRoom.guid, item.guid ], null ) }
													/>
												);
											}}
										/>
									</ThemedBlock>
								</Column>
							);
						})}
					</Row>
				</Container>
			</Grid>
		</View>
	);
}

export const styles = StyleSheet.create({
	rowMarginBottom6: {
		marginBottom: 6,
	},
	themedBlockTitlePaddingBottom0: {
		paddingBottom: 0,
	},
	viewHeight12MarginBottom4: {
		height: 12,
		marginBottom: 4,
		marginHorizontal: -3,
	},
	textShowAllTimeSlots: {
		color: colorList.ORANGE.primary,
		textDecorationLine: 'underline',
		marginLeft: 10
	},
});

export default ManagementDashboardComponentMeetingRooms
